import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Card, Grid, Icon, Image, Label, Popup } from 'semantic-ui-react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { signS3UrlForUser, signDerivativeForUser } from '../../utils/fetchSignedUrlByFolderV2';
import { CustomButton } from '../common';
import VariationBadgeList from './VariationBadgeList';
// Inline ⓥ trigger that opens the Character Context Hover Panel. Lazily
// mounts the popover only when activated, so per-card render cost stays
// minimal even on the marketplace's longer lists. See:
// docs/journal/PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §1.1
import CharacterContextTrigger from '../characterContext/CharacterContextTrigger';
import MergedNamesHint from './MergedNamesHint';


/**
 * OpportunityCard component displays a single opportunity in a card format
 * 
 * @param {Object} props - Component props
 * @param {Object} props.opportunity - The opportunity data to display
 * @param {Function} props.onApply - Function to call when the Apply button is clicked
 * @param {Function} props.onViewDetails - Function to call when the View Details button is clicked
 * @param {boolean} props.isCreator - Whether the current user is the creator of the opportunity
 * @param {boolean} props.hasApplied - Whether the current user has already applied for this opportunity
 * @param {string} props.backButtonText - Text for the back button on the target page (default: 'Back to Marketplace')
 * @param {boolean} props.isVoiceActor - Whether the current user's active role is voiceActor (required to apply)
 */
const OpportunityCard = ({
  opportunity,
  onApply,
  onViewDetails,
  isCreator = false,
  hasApplied = false,
  isVoiceActor = false,
  backButtonText = 'Back to Marketplace'
}) => {

  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector(state => state.user.profile?.data?.userId);
  const [bookCoverUrl, setBookCoverUrl] = useState('');
  const [coverFellBack, setCoverFellBack] = useState(false);

  /**
   * ⭐ FETCH THE `micro` DERIVATIVE, NOT THE ORIGINAL (2026-10-22).
   *
   * ⛔ This card renders the cover at Semantic `mini` and was fetching the full original to do
   * it. Measured: covers average **2,839 KB**, so a marketplace page of ten pulled **23.9 MB**.
   * The `micro` rung is **3,072 bytes**, which makes the same page **30 KB**.
   *
   * ⓘ `signDerivativeForUser` signs the derivative key and falls back to the original when no
   * derivative exists, so this is safe for an image the backfill has not covered — it gets
   * slower, never broken. ⚠️ And `onError` below is the second net: a derivative key that signs
   * but 404s still ends up showing the original.
   */
  useEffect(() => {
    let cancelled = false;
    const loadBookCover = async () => {
      const original = opportunity?.book?.profile?.bookCover;
      if (!original || !userId) return;
      try {
        const { url } = await signDerivativeForUser(userId, original, 'micro');
        if (!cancelled) { setBookCoverUrl(url); setCoverFellBack(false); }
      } catch (error) {
        console.error('Failed to load book cover:', error);
      }
    };

    loadBookCover();
    return () => { cancelled = true; };
  }, [opportunity?.book?.profile?.bookCover, userId]);

  /** ⛔ Last resort: a signed derivative that does not exist. Show the original rather than a gap. */
  const handleCoverError = async () => {
    if (coverFellBack) return;
    setCoverFellBack(true);
    const original = opportunity?.book?.profile?.bookCover;
    if (!original || !userId) return;
    try {
      setBookCoverUrl(await signS3UrlForUser(userId, original));
    } catch (e) {
      console.error('Cover fallback failed:', e);
    }
  };

  // Get application count from opportunity data (provided by backend)
  const applicationCount = opportunity.applicationCount || 0;

  // Format the deadline date if it exists
  const formattedDeadline = opportunity.deadline
    ? format(new Date(opportunity.deadline), 'MMM d, yyyy')
    : 'No deadline';

  // Get the status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'green';
      case 'Assigned':
        return 'blue';
      case 'Completed':
        return 'purple';
      case 'Cancelled':
        return 'red';
      default:
        return 'grey';
    }
  };

  // Get the priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'red';
      case 'Medium':
        return 'yellow';
      case 'Low':
        return 'blue';
      default:
        return 'grey';
    }
  };

  // Casting tier drives the badge colour. Mirrors OpportunitySpreadsheet's table deliberately —
  // the same role must not read as a different tier depending on which view you are in.
  const TIER_DISPLAY = {
    narration: { label: 'Narrator', color: 'violet' },
    lead: { label: 'Lead', color: 'red' },
    support: { label: 'Support', color: 'orange' },
    walkon: { label: 'Walk-on', color: 'grey' },
    nonspeaking: { label: 'Non-speaking', color: 'black' }
  };

  // The only card. 'compact' and 'standard' are gone: compact was a stripped-down duplicate
  // nobody wanted, and standard differed from detailed by ONE thing — a 150-character truncation
  // of a templated description. Measured 2026-08-31: 61% of descriptions are under 150 characters
  // anyway, so for most rows the two modes rendered identically, and where they differed the
  // extra text was the tail of a formula the actor had already read on the row above.
  return (
    <Card fluid >
      <Card.Content>
        <Card.Header>{opportunity.title}</Card.Header>

        <Card.Description style={{ marginTop: '8px', marginBottom: '12px' }}>
          {opportunity.description}
        </Card.Description>

        {/* Status, Priority, Applications, and Deadline on same line */}
        <Card.Meta style={{ marginBottom: '0px' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <Label color={getStatusColor(opportunity.status)} size="tiny">
              {opportunity.status}
            </Label>
            {opportunity.priority && (
              <Label color={getPriorityColor(opportunity.priority)} size="tiny">
                {opportunity.priority} Priority
              </Label>
            )}
            <Label basic size="tiny" color={applicationCount > 0 ? 'teal' : 'grey'}>
              <Icon name="users" />
              {applicationCount} {applicationCount === 1 ? 'Application' : 'Applications'}
            </Label>
            <span style={{ marginLeft: 'auto', fontSize: '0.9em', color: '#666' }}>
              <Icon name="calendar" /> {formattedDeadline}
            </span>
          </div>
        </Card.Meta>
      </Card.Content>

      {/* Book and Character info, Compensation details */}
      <Card.Content extra style={{ margin: 0, paddingTop: "0px" }}>
        <Grid style={{ margin: 0 }}>
          <Grid.Column width="9" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
            {opportunity.book && (
              <div>

                <div style={{ marginBottom: '4px', fontSize: '0.9em' }}>
                  <Icon name="book" /> Book: {opportunity.book.name}
                </div>
                <div style={{ marginBottom: '4px', fontSize: '0.9em' }}>
                  <Icon name="book" /> Author: {opportunity.book.profile?.author ? opportunity.book.profile.author : ""}
                </div>
              </div>
            )}
            {opportunity.character && (
              <div style={{ fontSize: '0.9em' }}>
                <Icon name="user" /> Character: {opportunity.character.name}
                <MergedNamesHint aliases={opportunity.character.aliases} />
                {/* Character Context Hover Panel trigger — clicking the ⓥ
                 * slides a context panel in from the right viewport edge.
                 * Mockup-driven for now (per useCharacterContext); same
                 * call site will resolve real data once the resolver
                 * services ship — no host-page change required at that
                 * point. */}
                <CharacterContextTrigger
                  bookUuid={opportunity.book?.uuid}
                  characterId={opportunity.character.uuid}
                  characterName={opportunity.character.name}
                  bookTitle={opportunity.book?.name}
                />

                {/* Casting tier, dialogue-line count and the derived voice profile.
                  *
                  * These were stamped on the Character by casting derivation and had never been
                  * surfaced on the card at all — so "Detailed" showed more boilerplate while
                  * omitting the only data an actor can judge a role by. Measured 2026-08-31: 879
                  * of 1,512 open roles are walk-ons of four lines or fewer, so without a size
                  * signal the 108 leads were indistinguishable from them.
                  *
                  * The em-dash reads as "not known yet" for series whose casting has not been
                  * derived, rather than as a missing value. */}
                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                  {TIER_DISPLAY[opportunity.character.castingTier] ? (
                    <Label size="tiny" color={TIER_DISPLAY[opportunity.character.castingTier].color}>
                      {TIER_DISPLAY[opportunity.character.castingTier].label}
                    </Label>
                  ) : (
                    <Label size="tiny" basic title="Casting has not been derived for this series yet">
                      &mdash;
                    </Label>
                  )}
                  <span style={{ fontSize: '0.85em', color: '#666' }}>
                    {opportunity.character.dialogueLineCount != null
                      ? `${opportunity.character.dialogueLineCount} dialogue line${opportunity.character.dialogueLineCount === 1 ? '' : 's'}`
                      : 'line count pending'}
                  </span>
                </div>
                {[opportunity.character.suggestedGender,
                  opportunity.character.suggestedAge,
                  opportunity.character.suggestedArchetype].filter(Boolean).length > 0 && (
                  <div style={{ marginTop: '3px', fontSize: '0.8em', color: '#999' }}>
                    {[opportunity.character.suggestedGender,
                      opportunity.character.suggestedAge,
                      opportunity.character.suggestedArchetype].filter(Boolean).join(' · ')}
                  </div>
                )}

              </div>
            )}

            {/* {opportunity.variations && opportunity.variations.length > 0 && (
              <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                <Icon name="theater masks" size="small" style={{ color: '#888', marginRight: '2px' }} />
                {opportunity.variations.map((v, i) => {
                  const { emoji } = getEmotionStyle(v.emotion);
                  return (
                    <span>{v.emotion}</span>
                    // <span key={v.uuid || i} style={emotionBadgeStyle(v.emotion)}>
                    //   {emoji} {v.emotion}
                    // </span>
                  );
                })}
              </div>
            )} */}
          </Grid.Column>
          <Grid.Column width="5" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
            {opportunity.compensation && (
              <div style={{ marginBottom: '4px', fontSize: '0.9em' }}>
                <Icon name="dollar" /> Compensation: {opportunity.compensation}
              </div>
            )}
            {opportunity.compensationType && (
              <div style={{ fontSize: '0.9em' }}>
                <Icon name="info circle" /> Type: {opportunity.compensationType}
              </div>
            )}
          </Grid.Column>
          <Grid.Column width="2" verticalAlign='top' textAlign='center' style={{ paddingTop: '8px', paddingBottom: '8px' }}>
            <div style={{ fontSize: '0.9em' }}>
              Cover
            </div>
            {bookCoverUrl && <Image src={bookCoverUrl} size='mini' onError={handleCoverError} />}
          </Grid.Column>
        </Grid>
        <Grid style={{ marginTop: "-10px", marginBottom: "10px" }} >
          <Grid.Column width="16">
            <VariationBadgeList variations={opportunity.variations} columns={5} />

          </Grid.Column>
        </Grid>
      </Card.Content>

      {/* Compressed buttons section */}
      <Card.Content extra style={{ padding: '10px 14px', marginTop: "-20px" }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <CustomButton
            color="blue"
            size="tiny"
            icon="eye"
            onClick={() => onViewDetails(opportunity.uuid)}
          >
            Details
          </CustomButton>
          {!isCreator && (
            opportunity.status !== 'Open' ? (
              <Popup
                content="This opportunity is no longer accepting applications"
                trigger={
                  <span>
                    <CustomButton color="grey" size="tiny" icon="paper plane" disabled>
                      Apply
                    </CustomButton>
                  </span>
                }
              />
            ) : hasApplied ? (
              <Popup
                content="You have already applied for this opportunity"
                trigger={
                  <span>
                    <CustomButton color="green" size="tiny" icon="check" disabled>
                      Applied
                    </CustomButton>
                  </span>
                }
              />
            ) : !isVoiceActor ? (
              <Popup
                content="You must be acting as a Voice Actor to apply for opportunities"
                trigger={
                  <span>
                    <CustomButton color="green" size="tiny" icon="paper plane" disabled>
                      Apply
                    </CustomButton>
                  </span>
                }
              />
            ) : (
              <CustomButton
                color="green"
                size="tiny"
                icon="paper plane"
                onClick={() => onApply(opportunity.uuid)}
              >
                Apply
              </CustomButton>
            )
          )}
          {isCreator && (
            <CustomButton
              color="blue"
              size="tiny"
              icon="users"
              onClick={() => navigate(`/opportunities/${opportunity.uuid}/applications`, {
                state: {
                  from: location.pathname + location.search,
                  backButtonText
                }
              })}
            >
              Applications
            </CustomButton>
          )}
        </div>
      </Card.Content>
    </Card >
  );
};

OpportunityCard.propTypes = {
  opportunity: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string,
    deadline: PropTypes.string,
    compensation: PropTypes.string,
    compensationType: PropTypes.string,
    book: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    }),
    character: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  }).isRequired,
  onApply: PropTypes.func,
  onViewDetails: PropTypes.func.isRequired,
  isCreator: PropTypes.bool,
  hasApplied: PropTypes.bool,
  isVoiceActor: PropTypes.bool,
  backButtonText: PropTypes.string
};

export default OpportunityCard;
